import { XIcon } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef } from "react";
import { cn } from "../lib/cn.js";
import { Button } from "./button.js";

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * Modal dialog on the native <dialog> element: built-in focus trap, Esc to close and focus
 * return, and no injected inline styles (CSP-friendly). Closes on backdrop click too.
 * Mark the field to focus first with `data-autofocus`.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      // showModal() focuses the first focusable element; prefer the one marked data-autofocus.
      dialog.querySelector<HTMLElement>("[data-autofocus]")?.focus();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the backdrop click's keyboard equivalent is Esc (onCancel)
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onCancel={(event) => {
        event.preventDefault();
        onOpenChange(false);
      }}
      onClick={(event) => {
        // A click on the dialog element itself (not its content) is a backdrop click.
        if (event.target === event.currentTarget) onOpenChange(false);
      }}
      className={cn(
        "m-auto w-[min(100%-2rem,32rem)] border bg-popover p-0 text-popover-foreground shadow-window backdrop:bg-scrim open:animate-in open:fade-in-0 open:duration-150",
        // Never taller than the visible screen (spec08 FR26): the content scrolls below a fixed
        // header, so long forms stay usable with the on-screen keyboard open. `open:` keeps the
        // closed dialog hidden.
        "max-h-[calc(100dvh-2rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] overflow-hidden open:flex open:flex-col",
        className,
      )}
    >
      {open && (
        <div className="flex min-h-0 flex-col gap-4 p-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h2 id={titleId} className="text-sm font-semibold text-foreground">
                {title}
              </h2>
              {description && (
                <p id={descriptionId} className="text-xs text-muted-foreground">
                  {description}
                </p>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Close"
              onClick={() => onOpenChange(false)}
            >
              <XIcon />
            </Button>
          </div>
          <div className="-mx-5 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5">
            {children}
          </div>
        </div>
      )}
    </dialog>
  );
}
